import { Settings } from "../../Settings";
import Fast from "../../util/Fast";
import Grid from "../grid/Grid";
import GridTile from "../grid/GridTile";
import Vector from "../math/Vector";
import PathPlan from "../path/plan/PathPlan";
import Character from "./Character";

export default class Position {
    /**
     * 
     * @param {Character} character 
     */
    constructor(character) {
        /**
         * @constant
         * @type {Character}
         */
        this.character = character;

        /**
         * The bottom left corner of the character in the world.
         * @constant
         * @type {Vector}
         */
        this.worldCoordinateOrigin = new Vector(0, 0);
        /**
         * The center of the character in the world.
         * @constant
         * @type {Vector}
         */
        this.worldCoordinateCenter = new Vector(0, 0);
        /**
         * @type {GridTile}
         */
        this.tile = null;
        /**
         * @type {Grid}
         */
        this.grid = null;
        /**
         * @type {boolean}
         */
        this.facingLeft = true;

        /**
         * @type {number}
         */
        this.gravityThisFrame = 0;

        /**
         * @constant
         * @type {Array.<PathPlan>}
         */
        this.pathPlan = [];

    }

    /**
     * @private
     * @param {Grid} grid 
     */
    setGrid(grid) {
        if (this.grid == grid) {
            return;
        }

        if (this.grid) {
            // Remove the character from the grid.
            this.grid.removeCharacter(this.character);
        }

        if (grid != null) {
            // Add the character to the grid.
            grid.addCharacter(this.character);
        }

        this.grid = grid;
    }

    castVerticalPositionToInteger() {
        this.worldCoordinateOrigin.y = Fast.floor(this.worldCoordinateOrigin.y);
        ////////this.onWorldCoordinateChange();  we're just casting to int, no need to recalculate stuff
        this.worldCoordinateCenter.y = this.worldCoordinateOrigin.y + Settings.tile.halfSize;
    }

    /**
     * @param {GridTile} tile 
     * @param {number} x 
     * @param {number} y 
     */
    setTileAndOriginPosition(tile, x, y) {
        this.worldCoordinateOrigin.set(x, y);

        this.tile = tile;
        this.onWorldCoordinateChange();
    }

    /**
     * Returns true if the miner is close enough to the tile orgin to have their position reset to origin
     * without looking jerky.
     * @returns {boolean}
     */
    isNearTileOrigin() {
        if (!this.tile) {
            return false;
        }

        const dx = this.worldCoordinateOrigin.x - this.tile.origin.x;
        const dy = this.worldCoordinateOrigin.y - this.tile.origin.y;

        return Math.abs(dx) <= Settings.tile.quarterSize && Math.abs(dy) <= Settings.tile.quarterSize;
    }

    /**
     * 
     */
    setPositionToTileOrigin() {
        if (!this.tile) {
            return;
        }

        this.setTileAndOriginPosition(this.tile, this.tile.origin.x, this.tile.origin.y);
    }

    // setPositionToClosestTileOrigin() {
    //     if (!this.tile) {
    //         return;
    //     }

    //     // if we are close to the origin of our tile, then use that.
    //     // if we are close to a neighbor tile, and if it is open, use that.
    //     const dx = this.worldCoordinateOrigin.x - this.tile.origin.x;
    //     const dy = this.worldCoordinateOrigin.y - this.tile.origin.y;

    //     console.log('setPositionToClosestTileOrigin() dx=' + dx + ' dy=' + dy);

    //     if (dx < Settings.tile.halfSize && dy < Settings.tile.halfSize) {
    //         console.log('setPositionToClosestTileOrigin() using tile origin.');
    //         this.setTileAndOriginPosition(this.tile, this.tile.origin.x, this.tile.origin.y);
    //     }

    //     const closestNeighbor = this.findClosestNeighbor();
    //     if (closestNeighbor) {
    //         console.log('setPositionToClosestTileOrigin() switching to neighbor tile.');

    //         this.tile = closestNeighbor;
    //     }

    //     this.setTileAndOriginPosition(this.tile, this.tile.origin.x, this.tile.origin.y);
    // }

    /**
     * @private
     * @returns {GridTile}
     */
    findClosestNeighbor() {
        const neighbors = this.tile.neighbors;
        let closest = null;
        let closestDist = 1000000;

        for (let i = 0; i < neighbors.length; i++) {
            const neighbor = neighbors[i];
            if (!neighbor || !neighbor.open) {
                continue;
            }

            const dist = this.tile.origin.distanceSquared(neighbor.origin);
            if (!closest || dist < closestDist) {
                closest = neighbor;
                closestDist = dist;
            }
        }

        return closest;
    }

    /**
     * @returns {boolean}
     */
    isCenteredOnTile() {
        return this.tile && this.tile.origin.equals(this.worldCoordinateOrigin);
    }

    /**
     * Updates the origin position of the character.
     * @param {Vector} coordinate 
     */
    setWorldCoordinateOrigin(coordinate) {
        this.worldCoordinateOrigin.copy(coordinate);        
        this.onWorldCoordinateChange();
    }
    /**
     * Updates the origin position of the character.
     * @param {number} x 
     * @param {number} y 
     */
    setWorldCoordinateOriginXY(x, y) {
        this.worldCoordinateOrigin.set(x, y);
        this.onWorldCoordinateChange();
    }

    /**
     * @param {number} movement 
     */
    moveX(movement) {
        this.worldCoordinateOrigin.addXY(movement, 0);
        this.onWorldCoordinateChange();
    }

    /**
     * @param {number} movement 
     */
    moveY(movement) {
        this.worldCoordinateOrigin.addXY(0, movement);
        // no need to clamp becuase x is unchanged.
        this.onWorldCoordinateChange();
    }


    /**
     * Set the x-coordinate to a specific value.
     * @param {number} value 
     */
    setX(value) {
        this.worldCoordinateOrigin.x = value;
        this.onWorldCoordinateChange();
    }

    /**
     * Set the y-coordinate to a specific value.
     * @param {number} value 
     */
    setY(value) {
        this.worldCoordinateOrigin.y = value;
        this.onWorldCoordinateChange();
    }

    /**
     * @param {number} moveX 
     * @param {number} moveY
     */
    moveXY(moveX, moveY) {
        this.worldCoordinateOrigin.addXY(moveX, moveY);
        this.onWorldCoordinateChange();
    }

    /**
     * @private
     */
    onWorldCoordinateChange() {
        this.worldCoordinateCenter.copy(this.worldCoordinateOrigin);
        this.worldCoordinateCenter.addXY(Settings.tile.halfSize, Settings.tile.halfSize);

        if (!this.tile) {
            console.log('Position.onWorldCoordinateChange() tile is null');
            return;
        }

        if (!this.tile.contains(this.worldCoordinateOrigin)) {
            // If we have a tile, see if we can do a quick check of the neighbors.
            // The vast majority of the time, the new coordinate will be within on of our neighbor tiles.

            const safeTile = this.tile; // Keep a reference to the tile in case we need it.

            this.tile = this.tile.findNeighborContaining(this.worldCoordinateOrigin);

            if (!this.tile) {
                // If we have a grid, let's see if the tile is in the grid.
                if (this.grid) {
                    this.tile = this.grid.findGridTile(this.worldCoordinateOrigin);
                }

                if (!this.tile) {
                    this.tile = safeTile.getWorldGrid().findGridTile(this.worldCoordinateOrigin);
                }
            }
        }

        if (this.tile) {
            this.setGrid(this.tile.grid);
        }
        else {
            console.log('Position.onWorldCoordinateChange() character lost their tile after move.');
            // We just nullified the tile, so we should reset the path, pathPlan, and grid.
            this.character.onInvalidGrid();
        }
    }

    // /**
    //  * Removes the character from the grid. This should be invoked when the character dies.
    //  */
    // removeFromGrid() {
    //     if (this.grid && this.character) {
    //         this.grid.removeCharacter(this.character);
    //         this.grid = null;
    //     }
    // }

    // isInMotion() {
    //     return this.pathPlan.length > 0;
    // }

    // /**
    //  * 
    //  * @param {Grid} grid 
    //  */
    // onGridUnload(grid) {
    //     if (this.pathPlan.length === 0) {
    //         return;
    //     }

    //     for (let i = 0; i < this.pathPlan.length; i++) {
    //         const plan = this.pathPlan[i];

    //     }
    // }

    resetPathPlan() {
        if (this.pathPlan.length > 0) {
            this.pathPlan.length = 0;

            // It's important that the character is standing on the tile origin when there is no path.
            this.setPositionToTileOrigin();
        }
    }

    resetFull() {
        this.resetPathPlan();
        this.setGrid(null);
        this.tile = null;
        this.gravityThisFrame = 0;
    }
}